/**
 * Virtual scrolling log viewer component.
 * Renders log lines with line numbers and monospace content.
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import type { CSSProperties, UIEvent } from 'react';

export interface HighlightMatch {
  lineIndex: number;
  startOffset: number;
  length: number;
}

interface LogViewerProps {
  logs: string[];
  highlightMatches?: HighlightMatch[];
  currentMatchIndex?: number;
  wrapText?: boolean;
  hideTimestamp?: boolean;
  darkTheme?: boolean;
}

// Match both ISO 8601 (2026-07-09T12:34:56.789+08:00) and
// Beijing-time format (2026-07-09 12:34:56,789) used by BVP backend
const TIMESTAMP_RE = /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}[.,]\d+(?:[+-]\d{2}:\d{2})?\s*/g;
const LINE_HEIGHT = 20;
const OVERSCAN = 10;

function stripTimestamp(text: string): string {
  return text.replace(TIMESTAMP_RE, '');
}

/** Split a line into segments: plain text and highlighted spans. */
function renderHighlightedLine(
  content: string,
  lineIndex: number,
  matches: HighlightMatch[],
  currentMatchIndex: number,
) {
  const lineMatches = matches.filter((m) => m.lineIndex === lineIndex);
  if (lineMatches.length === 0) return content;

  const sorted = [...lineMatches].sort((a, b) => a.startOffset - b.startOffset);
  const segments: React.ReactNode[] = [];
  let cursor = 0;

  sorted.forEach((m, i) => {
    if (m.startOffset > cursor) {
      segments.push(content.slice(cursor, m.startOffset));
    }
    const isActive =
      currentMatchIndex >= 0 &&
      matches[currentMatchIndex]?.lineIndex === lineIndex &&
      matches[currentMatchIndex]?.startOffset === m.startOffset;
    segments.push(
      <mark
        key={i}
        style={{
          backgroundColor: isActive ? '#e8910c' : '#ffe58f',
          color: 'inherit',
          padding: 0,
          borderRadius: 2,
        }}
      >
        {content.slice(m.startOffset, m.startOffset + m.length)}
      </mark>,
    );
    cursor = m.startOffset + m.length;
  });

  if (cursor < content.length) {
    segments.push(content.slice(cursor));
  }

  return <>{segments}</>;
}

const LogViewer: React.FC<LogViewerProps> = ({
  logs,
  highlightMatches = [],
  currentMatchIndex = -1,
  wrapText = false,
  hideTimestamp = false,
  darkTheme = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);

  // Auto-scroll to current match
  useEffect(() => {
    if (currentMatchIndex < 0 || !highlightMatches[currentMatchIndex]) return;
    const match = highlightMatches[currentMatchIndex];
    const top = match.lineIndex * LINE_HEIGHT;
    scrollRef.current?.scrollTo({ top: top - 100, behavior: 'smooth' });
  }, [currentMatchIndex, highlightMatches]);

  // Auto-scroll to bottom when new logs arrive (if already near bottom)
  const prevLogCount = useRef(logs.length);
  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const wasAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (logs.length > prevLogCount.current && wasAtBottom) {
      el.scrollTop = el.scrollHeight;
    }
    prevLogCount.current = logs.length;
  }, [logs.length]);

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setScrollTop(el.scrollTop);
    setClientHeight(el.clientHeight);
  }, []);

  // Virtual scroll: compute visible range from state (triggers re-render on scroll)
  const rangeStart = Math.max(0, Math.floor(scrollTop / LINE_HEIGHT) - OVERSCAN);
  const rangeEnd = Math.min(logs.length, Math.ceil((scrollTop + (clientHeight || 600)) / LINE_HEIGHT) + OVERSCAN);

  const containerStyle: CSSProperties = {
    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
    fontSize: 12,
    lineHeight: `${LINE_HEIGHT}px`,
    backgroundColor: darkTheme ? '#1e1e1e' : '#fafafa',
    color: darkTheme ? '#d4d4d4' : '#333',
    height: '100%',
    overflow: 'auto',
    position: 'relative',
  };

  const lineNumWidth = Math.max(3, String(logs.length).length) * 9 + 16;

  if (logs.length === 0) {
    return (
      <div style={containerStyle} className="flex items-center justify-center">
        <span style={{ color: darkTheme ? '#666' : '#bbb' }}>暂无日志</span>
      </div>
    );
  }

  const totalHeight = logs.length * LINE_HEIGHT;

  return (
    <div
      ref={scrollRef}
      style={containerStyle}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {logs.slice(rangeStart, rangeEnd).map((line, i) => {
          const idx = rangeStart + i;
          const displayContent = hideTimestamp ? stripTimestamp(line) : line;
          const hasHighlight = highlightMatches.some((m) => m.lineIndex === idx);

          return (
            <div
              key={idx}
              style={{
                position: 'absolute',
                top: idx * LINE_HEIGHT,
                left: 0,
                right: 0,
                height: LINE_HEIGHT,
                display: 'flex',
                whiteSpace: wrapText ? 'pre-wrap' : 'pre',
                wordBreak: wrapText ? 'break-all' : undefined,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: lineNumWidth,
                  minWidth: lineNumWidth,
                  textAlign: 'right',
                  paddingRight: 8,
                  color: darkTheme ? '#666' : '#999',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </span>
              <span style={{ flex: 1, overflow: wrapText ? undefined : 'hidden' }}>
                {hasHighlight
                  ? renderHighlightedLine(displayContent, idx, highlightMatches, currentMatchIndex)
                  : displayContent}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LogViewer;
